import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

function formatDate(dateYmd) {
  if (!dateYmd) return "";
  const [year, month, day] = String(dateYmd).split("-").map(Number);
  if (!year || !month || !day) return String(dateYmd);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatTimeRange(slot) {
  if (!slot?.startTime || !slot?.endTime) return "";
  return `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`;
}

function formatTime(timeValue) {
  const match = String(timeValue || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return String(timeValue || "");
  const hour = Number(match[1]);
  const minute = match[2];
  if (!Number.isFinite(hour)) return String(timeValue || "");
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minute} ${suffix}`;
}

function WaitlistClaimPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadPreview() {
      if (!token) {
        setError("This waitlist claim link is missing required information.");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/waitlist/claim/preview`,
          { params: { token } },
        );
        if (!cancelled) {
          setPreview(response.data);
          setError("");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || "This waitlist slot is no longer available.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const claimSlot = async () => {
    setClaiming(true);
    setError("");
    try {
      const response = await axios.post(`${API_BASE_URL}/api/waitlist/claim`, { token });
      setSuccess(response.data);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to claim this waitlist slot.");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column" }}>
      <CustomerHeader />
      <main style={{ flex: 1, width: "100%", maxWidth: "720px", margin: "0 auto", padding: "48px 18px" }}>
        <section style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "8px", boxShadow: "0 10px 25px rgba(15, 23, 42, 0.08)", overflow: "hidden" }}>
          <div style={{ background: "#1a659e", color: "white", padding: "24px" }}>
            <h1 style={{ margin: 0, fontSize: "24px", lineHeight: 1.2 }}>Claim Open Waitlist Slot</h1>
          </div>

          <div style={{ padding: "24px" }}>
            {loading && <p style={{ margin: 0, color: "#475569" }}>Checking this opened slot...</p>}

            {!loading && error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "14px", color: "#991b1b", fontWeight: 600 }}>
                {error}
              </div>
            )}

            {!loading && success && (
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "16px", color: "#166534" }}>
                <div style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>Appointment Updated</div>
                <div>{success.message}</div>
                {success.booking && (
                  <div style={{ marginTop: "14px", color: "#14532d", lineHeight: 1.7 }}>
                    <div><strong>Date:</strong> {formatDate(success.booking.appointmentDate)}</div>
                    <div><strong>Time:</strong> {formatTime(success.booking.startTime)} - {formatTime(success.booking.endTime)}</div>
                  </div>
                )}
              </div>
            )}

            {!loading && preview && !success && (
              <>
                <p style={{ marginTop: 0, color: "#334155", lineHeight: 1.6 }}>
                  This will move your backup appointment into a free time on that day, sized to your
                  service. The time shown as available is when the opening is, not a fixed appointment
                  time - check the exact time below before you confirm.
                </p>

                <div style={{ display: "grid", gap: "12px", marginBottom: "18px" }}>
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "14px" }}>
                    <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Requested Service</div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{preview.waitlist?.serviceName}</div>
                    <div style={{ color: "#475569", marginTop: "4px" }}>{preview.waitlist?.shopName}</div>
                  </div>

                  <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "8px", padding: "14px" }}>
                    <div style={{ fontSize: "12px", color: "#9a3412", marginBottom: "4px" }}>Current Backup Appointment</div>
                    <div style={{ fontWeight: 700, color: "#7c2d12" }}>{formatDate(preview.currentBooking?.appointmentDate)}</div>
                    <div style={{ color: "#9a3412", marginTop: "4px" }}>{formatTimeRange(preview.currentBooking)}</div>
                  </div>

                  <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "14px" }}>
                    <div style={{ fontSize: "12px", color: "#166534", marginBottom: "4px" }}>Time available that day</div>
                    <div style={{ color: "#166534" }}>
                      {formatDate(preview.openedWindow?.appointmentDate)} · {formatTimeRange(preview.openedWindow)}
                    </div>
                    <div style={{ fontSize: "12px", color: "#166534", marginTop: "10px", marginBottom: "4px" }}>Your appointment will be</div>
                    <div style={{ fontWeight: 700, color: "#14532d" }}>
                      {formatDate(preview.openedSlot?.appointmentDate) || formatDate(preview.openedWindow?.appointmentDate)} · {formatTimeRange(preview.openedSlot)}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={claimSlot}
                  disabled={claiming}
                  style={{ width: "100%", padding: "13px 18px", border: "none", borderRadius: "8px", background: claiming ? "#94a3b8" : "#1a659e", color: "white", fontWeight: 700, cursor: claiming ? "not-allowed" : "pointer" }}
                >
                  {claiming ? "Claiming Slot..." : "Move My Appointment to This Slot"}
                </button>
              </>
            )}
          </div>
        </section>
      </main>
      <CustomerFooter />
    </div>
  );
}

export default WaitlistClaimPage;